import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, Button, Stack, Chip, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Skeleton, TablePagination, alpha, useTheme,
  Snackbar, Alert, Switch,
} from '@mui/material';
import { Add, Edit, Delete, PriceChange } from '@mui/icons-material';
import OutstationPricingDrawer, { type OutstationPricingFormState } from './OutstationPricingDrawer';
import { getMasters, createMaster, updateMaster, deleteMaster } from '../../services/platform/masterService';
import { listZones } from '../../services/zoneService';
import { parseMasterListRows, vehiclesForOutstation } from '../../utils/masterRows';
import {
  formatKmSlab,
  groupOutstationPricing,
  buildZoneCodeResolver,
  buildOutstationPricingZoneResolver,
  zoneCodesMatch,
  patchOutstationPricingRows,
  isSecondaryOutstationZone,
  getPrimaryZoneForCode,
  listPrimaryOutstationZones,
  normalizeOutstationGstPercentage,
  normalizeOutstationMinimumKm,
  normalizeOutstationMaximumKm,
  normalizeOutstationBaseKm,
  normalizeOutstationSurgeMultiplier,
  validateOutstationGstPercentage,
  validateOutstationKmSlab,
  validateOutstationBaseKm,
  validateOutstationVehiclePricing,
  getDuplicateOutstationPricingMessage,
  getOverlappingOutstationPricingMessage,
  buildOutstationTollParkingMetadata,
  formatOutstationChargeType,
  resolveOutstationZoneCurrency,
  type OutstationPricingGroup,
  type OutstationPricingMasterRow,
} from '../../utils/outstationPricing';
import { useI18n } from '../../i18n/I18nProvider';
import { translateUiText } from '../../i18n/translateUiText';
import { formatMoney } from '../../utils/currency';

function apiErrorMessage(err: unknown, fallback: string) {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return ax.response?.data?.message || ax.message || fallback;
}

function formatTripType(t: string) {
  return t === 'round_trip' ? 'Round Trip' : 'One Way';
}

interface Props {
  lockedZoneCode?: string;
  title?: string;
  subtitle?: string;
}

export default function OutstationPricingPanel({ lockedZoneCode, title, subtitle }: Props) {
  const theme = useTheme();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [zoneFilter, setZoneFilter] = useState(lockedZoneCode || '');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<OutstationPricingGroup | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [togglingGroupKey, setTogglingGroupKey] = useState<string | null>(null);
  const [statusOverrides, setStatusOverrides] = useState<Record<string, boolean>>({});
  const [toast, setToast] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const { data: zonesData } = useQuery({
    queryKey: ['zones-outstation-pricing'],
    queryFn: () => listZones({ limit: 500 }),
  });
  const zones = zonesData?.rows || [];

  const resolveZoneCode = useMemo(() => buildZoneCodeResolver(zones), [zones]);
  const resolvePricingZoneCode = useMemo(() => buildOutstationPricingZoneResolver(zones), [zones]);
  const primaryZones = useMemo(() => listPrimaryOutstationZones(zones), [zones]);

  const lockedPrimaryZone = useMemo(
    () => (lockedZoneCode ? getPrimaryZoneForCode(lockedZoneCode, zones, resolveZoneCode) : undefined),
    [lockedZoneCode, zones, resolveZoneCode],
  );
  const isSecondaryLocked = useMemo(
    () => (lockedZoneCode ? isSecondaryOutstationZone(lockedZoneCode, zones, resolveZoneCode) : false),
    [lockedZoneCode, zones, resolveZoneCode],
  );
  const readOnly = isSecondaryLocked;

  const effectivePricingZone = lockedZoneCode
    ? resolvePricingZoneCode(lockedZoneCode)
    : (zoneFilter ? resolvePricingZoneCode(zoneFilter) : '');

  const zoneCurrency = useMemo(() => {
    const code = lockedPrimaryZone?.code || lockedZoneCode || effectivePricingZone;
    if (!code) return 'INR';
    return resolveOutstationZoneCurrency(code, zones, resolveZoneCode);
  }, [lockedPrimaryZone?.code, lockedZoneCode, effectivePricingZone, zones, resolveZoneCode]);

  const currencyForGroup = (group: OutstationPricingGroup) =>
    resolveOutstationZoneCurrency(group.zoneCode, zones, resolveZoneCode);

  const { data: vehicleTypeRows = [] } = useQuery({
    queryKey: ['vehicle-types-outstation-pricing'],
    queryFn: async () => {
      const res = await getMasters('vehicle_types', { limit: 200, page: 1 });
      return parseMasterListRows<{ code: string; name: string; metadata?: { serviceTypes?: string } }>(res);
    },
  });

  const { data: pricingData, isLoading } = useQuery({
    queryKey: ['outstation-pricing'],
    queryFn: async () => {
      const res = await getMasters('outstation_pricing', { limit: 500, page: 1 });
      return parseMasterListRows<{
        _id: string; code: string; name: string; isActive?: boolean; metadata?: Record<string, unknown>;
      }>(res);
    },
  });

  const vehicleNameMap = useMemo(() => {
    const map = new Map<string, string>();
    vehicleTypeRows.forEach((v) => map.set(v.code, v.name));
    return map;
  }, [vehicleTypeRows]);

  const outstationVehicleOptions = useMemo(
    () => vehiclesForOutstation(vehicleTypeRows.map((v) => ({
      code: v.code,
      name: v.name,
      serviceTypes: v.metadata?.serviceTypes,
    }))),
    [vehicleTypeRows],
  );

  const zoneNameMap = useMemo(() => {
    const map = new Map<string, string>();
    zones.forEach((z) => { if (z.code) map.set(z.code, z.name); });
    return map;
  }, [zones]);

  const allPricingGroups = useMemo(
    () => groupOutstationPricing(pricingData || [], zoneNameMap, vehicleNameMap, resolvePricingZoneCode),
    [pricingData, zoneNameMap, vehicleNameMap, resolvePricingZoneCode],
  );

  const allGroups = useMemo(() => {
    if (!effectivePricingZone) return allPricingGroups;
    return allPricingGroups.filter((group) => zoneCodesMatch(
      group.zoneCode,
      effectivePricingZone,
      resolveZoneCode,
    ));
  }, [allPricingGroups, effectivePricingZone, resolveZoneCode]);

  const pagedGroups = allGroups.slice(page * pageSize, page * pageSize + pageSize);
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['outstation-pricing'] });
    queryClient.invalidateQueries({ queryKey: ['outstation-pricing-zone-counts'] });
  };

  const saveMutation = useMutation({
    mutationFn: async ({ form, group }: { form: OutstationPricingFormState; group: OutstationPricingGroup | null }) => {
      const gstError = validateOutstationGstPercentage(form.gstPercentage);
      if (gstError) {
        throw new Error(gstError);
      }
      const kmSlabError = validateOutstationKmSlab(form.minimumKm, form.maximumKm);
      if (kmSlabError) {
        throw new Error(kmSlabError);
      }
      const gstPercentage = normalizeOutstationGstPercentage(form.gstPercentage);
      const minimumKm = normalizeOutstationMinimumKm(form.minimumKm);
      const maximumKm = normalizeOutstationMaximumKm(form.maximumKm);
      const baseKmError = validateOutstationBaseKm(form.baseKm, { minimumKm, maximumKm });
      if (baseKmError) {
        throw new Error(baseKmError);
      }
      const baseKm = normalizeOutstationBaseKm(form.baseKm, { minimumKm, maximumKm });
      const pricingZoneCode = resolvePricingZoneCode(form.zoneCode);
      if (!pricingZoneCode) {
        throw new Error('Select a valid primary zone for outstation pricing.');
      }
      const duplicateMessage = getDuplicateOutstationPricingMessage(allPricingGroups, {
        zoneCode: pricingZoneCode,
        tripType: form.tripType,
        minimumKm,
        maximumKm,
        excludeGroupCode: group?.pricingGroupCode,
        resolveZoneCode: resolvePricingZoneCode,
      });
      if (duplicateMessage) {
        throw new Error(duplicateMessage);
      }
      const overlapMessage = getOverlappingOutstationPricingMessage(allPricingGroups, {
        zoneCode: pricingZoneCode,
        tripType: form.tripType,
        minimumKm,
        maximumKm,
        excludeGroupCode: group?.pricingGroupCode,
        resolveZoneCode: resolvePricingZoneCode,
      });
      if (overlapMessage) {
        throw new Error(overlapMessage);
      }
      const vehiclePricingError = validateOutstationVehiclePricing(form.vehiclePrices, {
        expectedVehicleCount: outstationVehicleOptions.length,
      });
      if (vehiclePricingError) {
        throw new Error(vehiclePricingError);
      }
      const ops = form.vehiclePrices.map((vp) => {
        const code = vp.rowCode || `OS_${pricingZoneCode}_${vp.vehicleTypeCode}_${form.tripType}`.toUpperCase();
        const vName = vehicleNameMap.get(vp.vehicleTypeCode) || vp.vehicleTypeCode;
        const name = `${zoneNameMap.get(pricingZoneCode) || pricingZoneCode} · ${vName} · ${formatTripType(form.tripType)}`;
        const payload = {
          code,
          name,
          isActive: group?.isActive ?? true,
          metadata: {
            zoneCode: pricingZoneCode,
            pricingGroupCode: form.pricingGroupCode,
            vehicleTypeCode: vp.vehicleTypeCode,
            tripType: form.tripType,
            minimumKm,
            maximumKm,
            baseKm,
            baseFare: vp.baseFare,
            perKmRate: vp.perKmRate,
            driverBataPerDay: vp.driverBataPerDay,
            nightCharge: vp.nightCharge,
            waitingChargePerMinute: vp.waitingChargePerMinute,
            ...buildOutstationTollParkingMetadata(form.tollType, form.parkingType),
            gstPercentage,
            surgeEnabled: form.surgeEnabled,
            surgeMultiplier: normalizeOutstationSurgeMultiplier(form.surgeMultiplier, form.surgeEnabled),
            status: (group?.isActive ?? true) ? 'active' : 'inactive',
          },
        };
        if (vp.rowId) return updateMaster(vp.rowId, payload);
        return createMaster('outstation_pricing', payload);
      });

      const existingCodes = new Set(form.vehiclePrices.map((v) => v.vehicleTypeCode));
      const removeOps = (group?.vehiclePrices || [])
        .filter((vp) => vp.rowId && !existingCodes.has(vp.vehicleTypeCode))
        .map((vp) => deleteMaster(vp.rowId!));

      await Promise.all([...ops, ...removeOps]);
    },
    onSuccess: (_data, { form, group }) => {
      invalidate();
      setDrawerOpen(false);
      setEditingGroup(null);
      const action = group ? 'updated' : 'created';
      const tripLabel = form.tripType === 'round_trip' ? 'Round Trip' : 'One Way';
      const zoneName = zoneNameMap.get(form.zoneCode) || form.zoneCode;
      setToast({
        msg: `Outstation pricing ${action} successfully for ${zoneName} · ${tripLabel}.`,
        severity: 'success',
      });
    },
    onError: (err) => setToast({ msg: apiErrorMessage(err, 'Failed to save'), severity: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (group: OutstationPricingGroup) => {
      await Promise.all(
        group.vehiclePrices.filter((vp) => vp.rowId).map((vp) => deleteMaster(vp.rowId!)),
      );
    },
    onSuccess: () => { invalidate(); setToast({ msg: 'Pricing deleted', severity: 'success' }); },
    onError: (err) => setToast({ msg: apiErrorMessage(err, 'Delete failed'), severity: 'error' }),
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ group, isActive }: { group: OutstationPricingGroup; isActive: boolean }) => {
      const cached = queryClient.getQueryData<OutstationPricingMasterRow[]>(['outstation-pricing']) || [];
      const status = isActive ? 'active' : 'inactive';
      await Promise.all(
        group.vehiclePrices.filter((vp) => vp.rowId).map((vp) => {
          const row = cached.find((r) => r._id === vp.rowId);
          return updateMaster(vp.rowId!, {
            isActive,
            metadata: { ...(row?.metadata || {}), status },
          });
        }),
      );
    },
    onMutate: async ({ group, isActive }) => {
      setTogglingGroupKey(group.groupKey);
      setStatusOverrides((prev) => ({ ...prev, [group.groupKey]: isActive }));
      await queryClient.cancelQueries({ queryKey: ['outstation-pricing'] });
      const previous = queryClient.getQueryData<OutstationPricingMasterRow[]>(['outstation-pricing']);
      if (previous) {
        queryClient.setQueryData(
          ['outstation-pricing'],
          patchOutstationPricingRows(previous, group, isActive),
        );
      }
      return { previous, groupKey: group.groupKey };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['outstation-pricing-zone-counts'] });
    },
    onError: (err, { group }, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['outstation-pricing'], context.previous);
      }
      const groupKey = context?.groupKey ?? group.groupKey;
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[groupKey];
        return next;
      });
      setToast({ msg: apiErrorMessage(err, 'Failed to update status'), severity: 'error' });
    },
    onSettled: (_data, _error, { group }) => {
      setTogglingGroupKey(null);
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[group.groupKey];
        return next;
      });
    },
  });

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }} spacing={1}>
        <Box>
          <Typography variant="h6" fontWeight={800}>{translateUiText(title || 'Outstation Pricing Master', t)}</Typography>
          <Typography variant="body2" color="text.secondary">
            {translateUiText(subtitle || 'KM slab + trip type packages with per-vehicle pricing — same layout as rental management.', t)}
          </Typography>
        </Box>
        <Button size="small" variant="contained" startIcon={<Add />} disabled={readOnly}
          onClick={() => { setEditingGroup(null); setDrawerOpen(true); }}>
          Add Pricing
        </Button>
      </Stack>

      {readOnly && lockedPrimaryZone && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Outstation pricing is configured on the primary zone only. This secondary zone inherits pricing from{' '}
          <strong>{lockedPrimaryZone.name} ({lockedPrimaryZone.code})</strong>.
        </Alert>
      )}

      {!lockedZoneCode && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          <Chip label="All zones" onClick={() => { setZoneFilter(''); setPage(0); }} color={!zoneFilter ? 'primary' : 'default'} variant={!zoneFilter ? 'filled' : 'outlined'} />
          {primaryZones.map((z) => (
            <Chip key={z._id} label={z.name} onClick={() => { setZoneFilter(z.code || ''); setPage(0); }}
              color={zoneFilter === z.code ? 'primary' : 'default'} variant={zoneFilter === z.code ? 'filled' : 'outlined'} />
          ))}
        </Stack>
      )}

      {isLoading ? (
        <Stack spacing={1}>{[1, 2, 3].map((i) => <Skeleton key={i} height={52} />)}</Stack>
      ) : !allGroups.length ? (
        <Paper elevation={0} sx={{ py: 6, textAlign: 'center', border: 1, borderColor: 'divider', borderStyle: 'dashed', borderRadius: 2.5 }}>
          <PriceChange sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="subtitle1" fontWeight={700}>No outstation pricing configured</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            {readOnly
              ? 'Configure outstation pricing on the primary zone to apply it here.'
              : 'Add a KM slab package and set fares for all vehicles in one step.'}
          </Typography>
          {!readOnly && (
          <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingGroup(null); setDrawerOpen(true); }}>
            Add First Pricing
          </Button>
          )}
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, width: 56 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Trip / KM Slab</TableCell>
                  {!lockedZoneCode && <TableCell sx={{ fontWeight: 700 }}>Zone</TableCell>}
                  <TableCell sx={{ fontWeight: 700 }}>Vehicles & Pricing</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Toll / Parking</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>GST</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 160 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedGroups.map((group, index) => {
                  const isActive = statusOverrides[group.groupKey] ?? group.isActive;
                  return (
                  <TableRow key={group.groupKey} hover sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) } }}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">{page * pageSize + index + 1}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Chip size="small" label={formatTripType(group.tripType)}
                            color={group.tripType === 'round_trip' ? 'secondary' : 'primary'} variant="outlined" />
                          <Typography variant="body2" fontWeight={700}>{formatKmSlab(group.minimumKm, group.maximumKm)}</Typography>
                        </Stack>
                        {group.baseKm > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            Base {group.baseKm} km included
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">{group.pricingGroupCode}</Typography>
                      </Stack>
                    </TableCell>
                    {!lockedZoneCode && (
                      <TableCell>
                        <Typography variant="body2">{group.zoneName}</Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {group.vehiclePrices.map((vp) => (
                          <Chip
                            key={vp.vehicleTypeCode}
                            size="small"
                            variant="outlined"
                            label={`${vp.vehicleTypeName} ${formatMoney(vp.baseFare, currencyForGroup(group))}+${formatMoney(vp.perKmRate, currencyForGroup(group))}/km`}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Chip
                          size="small"
                          variant={group.tollType === 'actual' ? 'filled' : 'outlined'}
                          label={`Toll: ${formatOutstationChargeType(group.tollType)}`}
                          color={group.tollType === 'actual' ? 'warning' : 'success'}
                        />
                        <Chip
                          size="small"
                          variant={group.parkingType === 'actual' ? 'filled' : 'outlined'}
                          label={`Parking: ${formatOutstationChargeType(group.parkingType)}`}
                          color={group.parkingType === 'actual' ? 'warning' : 'success'}
                        />
                        {group.tollType === 'actual' && (
                          <Typography variant="caption" color="warning.main" display="block">
                            Toll billed at actual cost
                          </Typography>
                        )}
                        {group.parkingType === 'actual' && (
                          <Typography variant="caption" color="warning.main" display="block">
                            Parking billed at actual cost
                          </Typography>
                        )}
                        {group.surgeEnabled && group.surgeMultiplier > 1 && (
                          <Chip
                            size="small"
                            variant="filled"
                            color="error"
                            label={`Surge ×${group.surgeMultiplier}`}
                          />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{group.gstPercentage}%</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch size="small" checked={isActive}
                          disabled={readOnly || togglingGroupKey === group.groupKey}
                          onChange={(e) => toggleMutation.mutate({ group, isActive: e.target.checked })} />
                        <Chip size="small" label={isActive ? 'Active' : 'Inactive'} color={isActive ? 'success' : 'default'} />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      {!readOnly && (
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button size="small" variant="outlined" startIcon={<Edit fontSize="small" />}
                          onClick={() => { setEditingGroup(group); setDrawerOpen(true); }}>
                          Edit
                        </Button>
                        <Button size="small" color="error" variant="outlined" startIcon={<Delete fontSize="small" />}
                          onClick={() => {
                            if (window.confirm('Delete this pricing slab and all vehicle fares?')) {
                              deleteMutation.mutate(group);
                            }
                          }}>
                          Delete
                        </Button>
                      </Stack>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={allGroups.length} page={page} onPageChange={(_, p) => setPage(p)}
            rowsPerPage={pageSize} onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]} />
        </Paper>
      )}

      <OutstationPricingDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingGroup(null); }}
        editingGroup={editingGroup}
        existingGroups={allPricingGroups}
        zones={primaryZones}
        vehicles={vehicleTypeRows.map((v) => ({ code: v.code, name: v.name, serviceTypes: v.metadata?.serviceTypes }))}
        lockedZoneCode={lockedPrimaryZone?.code || lockedZoneCode}
        defaultCurrency={zoneCurrency}
        resolvePricingZoneCode={resolvePricingZoneCode}
        loading={saveMutation.isPending}
        onSave={(form, group) => saveMutation.mutate({ form, group })}
      />

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)} sx={{ width: '100%' }}>
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
